/**
 * External Users API
 * Client-side API for external user management cloud functions
 */
import { get, getDatabase, ref } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { recordActivity } from './activityApi';
import {
    ExternalUser,
    CreateExternalUserPayload,
    UpdateExternalUserPayload,
} from '../types/guestAccess';

type ExternalUserAction = 'create' | 'update' | 'delete';

interface ApiRequest {
    action: ExternalUserAction;
    [key: string]: unknown;
}

interface CreateResponse {
    success: true;
    uid: string;
}

interface SuccessResponse {
    success: true;
}

/**
 * API for external user management (admin only)
 */
class ExternalUsersApi {
    private functions = getFunctions(undefined, 'europe-north1');

    private async call<R>(request: ApiRequest): Promise<R> {
        const externalUserApi = httpsCallable<ApiRequest, R>(
            this.functions,
            'externalUserApi'
        );
        const result = await externalUserApi(request);
        return result.data;
    }

    /**
     * Create a new external user
     */
    async create(payload: CreateExternalUserPayload): Promise<string> {
        const response = await this.call<CreateResponse>({
            action: 'create',
            ...payload,
        });
        recordActivity('externalUser.created', { targetKey: response.uid });
        return response.uid;
    }

    /**
     * Update an existing external user
     */
    async update(payload: UpdateExternalUserPayload): Promise<void> {
        await this.call<SuccessResponse>({
            action: 'update',
            ...payload,
        });
        recordActivity('externalUser.updated', { targetKey: payload.uid });
    }

    /**
     * List active external users, sorted by name.
     *
     * Read directly from users/userList (readable by every registered user)
     * rather than through the callable, so opening a picker never waits on a
     * function cold start. Soft-deleted nodes are kept for name resolution and
     * left out here.
     */
    async list(): Promise<ExternalUser[]> {
        const snapshot = await get(ref(getDatabase(), 'users/userList'));
        const data = snapshot.val() as Record<string, unknown> | null;

        const users = Object.entries(data || {})
            .map(([key, value]) => this.parseExternalUser(value, key))
            .filter((u): u is ExternalUser => u !== null);

        users.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
        return users;
    }

    private parseExternalUser(data: unknown, key: string): ExternalUser | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        if (d.isExternal !== true || d.deleted === true) return null;
        if (typeof d.name !== 'string') {
            console.warn(`Skipping external user ${key} without a name`);
            return null;
        }
        return {
            ...d,
            uid: key,
            name: d.name,
            email: typeof d.email === 'string' ? d.email : '',
            isExternal: true,
            createdAt: typeof d.createdAt === 'string' ? d.createdAt : '',
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
        } as ExternalUser;
    }

    /**
     * Delete an external user (soft delete). Active access links are revoked
     * automatically. The retained node is renamed to `replacementName` (or
     * "Borttagen användare") for historical name resolution.
     */
    async delete(uid: string, replacementName?: string): Promise<void> {
        await this.call<SuccessResponse>({
            action: 'delete',
            uid,
            ...(replacementName ? { replacementName } : {}),
        });
        recordActivity('externalUser.deleted', { targetKey: uid });
    }
}

export const externalUsersApi = new ExternalUsersApi();
